<template>
<master-layout pageTitle="Manage Team Members">
  <div class="chat-toolbar">
    <div class="toolbar-start">
      <VIcon name="arrow-left-line" :size="-4" @click="() => $router.go(-1)" class="cursorPointer"></VIcon>
      <h2 class="vtext-page-title--regular vml-20">{{ (dummyGroup.id ? 'Edit' : 'Add') + ' Group' }}</h2>
    </div>
    <div>
      <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Save" @click="updateGroup"></VButton>
    </div>
  </div>
  <div class="gridMargin">
    <div class="vmb-20">
      <div class="vcol vcol--no-padding vcol-xl-8 vcol-lg-12 vcol-md-12 vcol-sm-12 vcol-xs-12">
        <div class="model-sections">
          <div class="previewHeader">
            <span class="vtext-card-title--light">Team member details</span>
          </div>
          <div class="sectionPadding">
            <div class="flex-sections">
              <div class="section_divider">
                <h2 class="vtext-subtitle vpb-10">Group name (Max 30 characters)</h2>
                <div class="counter-wrapper">
                  <VTextField v-model="dummyGroup.name" placeholder="Group name" aria-label="group name" :maxlength="30" appearance="ghost" class="vivid-input"></VTextField>
                  <div class="custom-counter">
                      {{ (dummyGroup.name || '').length }}/30
                  </div>
                </div>
              </div>
              <div class="section_divider">
                <h2 class="vtext-subtitle vpb-10">Report to</h2>
                <selectDropDown
                  :options="reportToOptions"
                  :selectedValueProp="dummyGroup.report_to"
                  placeholder="Select role"
                  @onSelectOption="onReportToSelect"
                />
              </div>
              <div class="section_divider">
                <div class="flex-start vpb-10">
                  <h2 class="vtext-subtitle">Conversation routing method</h2>
                  <HelpTooltip>
                    <strong>Automatic Routing: </strong>
                    <ul>
                      <li>When chats are assigned to a group of agents, Jumper will automatically assign conversations equally amongst ALL agents in a group. Conversations will not flow into the Requests Tab. </li>
                    </ul>
                    <strong>Manual Routing: </strong>
                    <ul>
                      <li> When chats are transferred to a group of agents, Jumper will pass the chats to the Requests tab so that agents can pick up conversations manually.</li>
                    </ul>
                  </HelpTooltip>
                </div>
                <selectDropDown
                  :options="routingOptions"
                  :selectedValueProp="dummyGroup.routing"
                  placeholder="Select Conversation Routing Method"
                  @onSelectOption="({ selectedValue }) => dummyGroup.routing = selectedValue"
                />
              </div>
              <div class="section_divider">
                <div class="flex-start vpb-10">
                  <h2 class="vtext-subtitle">Show reporting group chats</h2>
                  <HelpTooltip>
                    Eg. Group A reports to Group B.
                    Do you want chats of Group A to be visible to members of Group B? If so, choose Yes in settings of Group B, else choose No.
                  </HelpTooltip>
                </div>
                <selectDropDown
                  :options="yesNoOptions"
                  :selectedValueProp="dummyGroup.heirarchy"
                  placeholder="Select Yes/No"
                  @onSelectOption="({ selectedValue }) => dummyGroup.heirarchy = selectedValue === 'true'"
                />
              </div>
              <div class="section_divider">
                <div class="flex-start vpb-10">
                  <h2 class="vtext-subtitle">Assign to previous team member</h2>
                  <HelpTooltip>
                    Eg. If a conversation was assigned to 'Agent A' previously and now its not assigned to anyone and the same customer comes back looking for live support, and this setting is set to 'Yes', then we will assign this conversation to same 'Agent A'
                  </HelpTooltip>
                </div>
                <selectDropDown
                  :options="yesNoOptions"
                  :selectedValueProp="dummyGroup.previous_agent"
                  placeholder="Select Yes/No"
                  @onSelectOption="({ selectedValue }) => dummyGroup.previous_agent = selectedValue === 'true'"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Permissions -->
    <div class="vrow">
      <div class="vcol vcol--no-padding vcol-xl-8 vcol-lg-12 vcol-md-12 vcol-sm-12 vcol-xs-12">
        <div class="model-sections">
          <div class="previewHeader">
            <p class="vtext-card-title--light vpb-10">Permissions</p>
            <p class="vtext-subtitle">Select permissions you want to give to this member.</p>
          </div>
          <div class="sectionPadding">
            <div v-for="(value, propertyName, index) in rolesDictionary" :key="index">
              <!-- Single permission (boolean) -->
              <div v-if="typeof rolesDictionary[propertyName]['selected'] === 'boolean'" class="permission-row cust-brdr vmb-20">
                <div class="permission-item">
                  <span class="vtext-subtitle">{{ rolesDictionary[propertyName]['label'] }}</span>
                  <VCheckbox :checked="rolesDictionary[propertyName]['selected']" @change="(e) => rolesDictionary[propertyName]['selected'] = e.target.checked" connotation="cta"></VCheckbox>
                </div>
              </div>
              <!-- Group of permissions -->
              <div v-else>
                <div class="flex-start gap10P vpb-10">
                  <p class="vtext-subtitle">{{ rolesDictionary[propertyName]['label'] }}</p>
                  <div class="checkBoxTitle" v-if="Object.keys(filtertedValue(value)).length > 1"><a @click="selectAll(true, propertyName)">Check all</a> | <a @click="selectAll(false, propertyName)">Un-check all</a></div>
                </div>
                <div class="permissions-grid cust-brdr vmb-20">
                  <div v-for="(value2, propertyName2, index2) in filtertedValue(value)" :key="index2" class="permission-item">
                    <span class="vtext-subtitle">{{ rolesDictionary[propertyName][propertyName2]['label'] }}</span>
                    <VCheckbox :checked="rolesDictionary[propertyName][propertyName2]['selected']" @change="(e) => { rolesDictionary[propertyName][propertyName2]['selected'] = e.target.checked; selectOption(); }" connotation="cta"></VCheckbox>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</master-layout>
</template>

<script>
import selectDropDown from '@/components/selectDropDown.vue';
import HelpTooltip from "@/components/HelpTooltip.vue";
import apiInterceptor from '@/shared/apiInterceptor';
import { queryClient } from '@/shared/queryClient';
import { businessRolesQueryOptions } from '@/composables/useBusinessRoles';
import rolesDictionary from '@/resources/js/rolesDictionary';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import { environment } from '@/shared/env';

export default {
  components: {
    selectDropDown,
    HelpTooltip
  },
  data() {
    return {
      allRoles: [],
      selectedOption: false,
      dummyGroup: {
        admin: false,
        default: false,
        dynamics: [],
        heirarchy: false,
        previous_agent: true,
        route_to_online_agents_only: true,
        id: undefined,
        name: undefined,
        permissions: {},
        report_to: [],
        routing: "automatic"
      },
      rolesDictionary
    }
  },
  mounted() {
    this.init();
    for(let prop in this.rolesDictionary) {
      if(typeof this.rolesDictionary[prop] === 'object' && this.rolesDictionary[prop].hasOwnProperty('selected')) {
        if(!this.permissions[prop]) delete this.rolesDictionary[prop];
      } else if (typeof this.rolesDictionary[prop] === 'object') {
        for (let prop2 in this.rolesDictionary[prop]) {
          if (typeof this.rolesDictionary[prop][prop2] === 'object') {
            if(!this.permissions[prop2]) delete this.rolesDictionary[prop][prop2];
          }
        }
        if(Object.keys(this.rolesDictionary[prop]).length === 1) delete this.rolesDictionary[prop];
      }
    }
    this.selectAll(false);
  },
  computed: {
    permissions() { return useAuthStore().getPermissions ?? {}; },
    reportToOptions() {
      return this.allRoles
        .filter(role => role.id !== this.dummyGroup.id)
        .map(role => ({ id: role.id, value: role.id, title: role.name }));
    },
    routingOptions() {
      return [
        { id: 'automatic', value: 'automatic', title: 'Automatic Routing' },
        { id: 'manual', value: 'manual', title: 'Manual Routing' }
      ];
    },
    yesNoOptions() {
      return [
        { id: 'true', value: 'true', title: 'Yes' },
        { id: 'false', value: 'false', title: 'No' }
      ];
    }
  },
  methods: {
    onReportToSelect({ selectedValue }) {
      this.dummyGroup.report_to = selectedValue;
    },
    selectAll(n, propName) {
      if (propName) {
        for (let propertyName2 in this.rolesDictionary[propName]) {
          if (typeof this.rolesDictionary[propName][propertyName2] === 'object') {
            this.rolesDictionary[propName][propertyName2]['selected'] = n;
          }
        }
      } else {
        for (let propertyName in this.rolesDictionary) {
          if (typeof this.rolesDictionary[propertyName] === 'object' && this.rolesDictionary[propertyName].hasOwnProperty('selected')) {
            this.rolesDictionary[propertyName]['selected'] = n;
          } else if (typeof this.rolesDictionary[propertyName] === 'object') {
            for (let propertyName2 in this.rolesDictionary[propertyName]) {
              if (typeof this.rolesDictionary[propertyName][propertyName2] === 'object') {
                this.rolesDictionary[propertyName][propertyName2]['selected'] = n;
              }
            }
          }
        }
      }
    },
    async init() {
      try {
        useUIStore().updateLoading({ isLoading: true });
        this.allRoles = await queryClient.ensureQueryData(businessRolesQueryOptions);
        if(this.$route.query?.groupid) {
          let businessRoleShowResponse = await apiInterceptor.get( environment("VUE_APP_API_URL") + "roles/v1/business_roles/" + this.$route.query.groupid);
          this.dummyGroup = businessRoleShowResponse;
          delete this.dummyGroup.success;
          delete this.dummyGroup.errorMessage;
          this.dummyGroup.report_to = this.dummyGroup.report_to[0].id;
          for (let thisPermission in this.rolesDictionary) {
            if (typeof this.rolesDictionary[thisPermission]['selected'] === 'boolean') {
              this.rolesDictionary[thisPermission]['selected'] = this.dummyGroup.permissions[thisPermission];
            } else {
              for (let thisPermission2 in this.rolesDictionary[thisPermission]) {
                if (thisPermission2 !== 'label') {
                  this.rolesDictionary[thisPermission][thisPermission2]['selected'] = this.dummyGroup.permissions[thisPermission2];
                }
              }
            }
          }
        }
      } catch (error) {
        useUIStore().updateToast({ open: true, message: error.message || "Something went wrong", type: "danger" })
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async updateGroup() {
      try {
        useUIStore().updateLoading({ isLoading: true });
        let permissions = {};
        let noPermissionSelected = true;
        for(let thisPermission in this.rolesDictionary){
          if(typeof this.rolesDictionary[thisPermission]['selected'] === 'boolean') {
            permissions[thisPermission] = this.rolesDictionary[thisPermission]['selected'];
            if(noPermissionSelected && permissions[thisPermission]) noPermissionSelected = false;
          } else {
            for(let thisPermission2 in this.rolesDictionary[thisPermission]) {
              if(thisPermission2 !== 'label') {
                permissions[thisPermission2] = this.rolesDictionary[thisPermission][thisPermission2]['selected'];
                if(noPermissionSelected && permissions[thisPermission2]) noPermissionSelected = false;
              }
            }
          }
        }
        let reqBody = {
          name: this.dummyGroup.name,
          report_to: this.dummyGroup.report_to,
          routing: this.dummyGroup.routing,
          heirarchy: this.dummyGroup.heirarchy,
          previous_agent: this.dummyGroup.previous_agent,
          route_to_online_agents_only: true,
          permissions: permissions,
        }
        if(!reqBody.name || reqBody.name.length === 0) {
          return useUIStore().updateToast({ open: true, message: "Please add a valid group name", type: "warning" });
        }
        if(!reqBody.report_to || reqBody.report_to.length === 0) {
          return useUIStore().updateToast({ open: true, message: "Please select report to", type: "warning" });
        }
        if(noPermissionSelected) {
          return useUIStore().updateToast({ open: true, message: "Please select atleast one permission", type: "warning" });
        }
        let apiUrl;
        if(this.dummyGroup.id){
          apiUrl = `${environment("VUE_APP_API_URL")}roles/v1/business_roles/${this.dummyGroup.id}`;
        } else {
          apiUrl = `${environment("VUE_APP_API_URL")}roles/v1/business_roles/`;
        }
        const apiCall = this.dummyGroup.id ? apiInterceptor.patch : apiInterceptor.post;
        let groupSaveResponse = await apiCall(apiUrl, reqBody);
        queryClient.invalidateQueries({ queryKey: ['business-roles'] });
        queryClient.invalidateQueries({ queryKey: ['livechat-business-roles'] });
        if(this.dummyGroup.id && groupSaveResponse.id == this.dummyGroup.id) {
          useUIStore().updateToast({
            open: true,
            message: "Group updated successfully!",
            type: "success"
          });
        } else {
          useUIStore().updateToast({ open: true, message: "Group added successfully!", type: "success" });
        }
        this.dummyGroup.name = undefined;
        this.dummyGroup.id = undefined;
        this.dummyGroup.routing = "automatic";
        this.dummyGroup.report_to = [];
        this.dummyGroup.permissions = {};
        this.dummyGroup.dynamics = [];
        this.selectAll(false);
        this.$router.push({ path: '/settings/team' });
      } catch (error) {
        useUIStore().updateToast({ open: true, message: error.message || "Something went wrong", type: "danger" });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    selectOption() {
      this.selectedOption = true;
    },
    filtertedValue(value) {
      return Object.keys(value)
      .filter(key => typeof value[key] === 'object')
      .reduce((obj, key) => {
        obj[key] = value[key];
        return obj;
      }, {});
    }
  }
}
</script>

<style lang="scss" scoped src="@/resources/css/settings.scss"> </style>

<style scoped>
.chat-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--white);
  border-bottom: 1px solid var(--background-primary);
}

.toolbar-start {
  display: flex;
  align-items: center;
}
.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}

.permission-row {
  padding: 0;
}

.permission-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--background-primary);
  border-radius: 5px;
  padding: 10px 15px;
  margin: 0 20px 20px 0;
}

.permissions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0;
  padding-bottom: 20px;
}

.checkBoxTitle {
  display: inline-block;
  font-size: 14px;
  line-height: 16px;
  color: var(--purple);
  font-weight: 400;
}

.checkBoxTitle a {
  text-decoration: none;
  color: var(--purple);
  cursor: pointer;
}
</style>
